import { Alert, Button, Modal } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { ProjectEducationGuideBody } from "../../../ProjectEducationPage/components/ProjectEducationGuideBody.tsx"
import { formatEditionDate } from "./education-track-helpers.ts"
import type { EditionView } from "./types/education.types.ts"

interface CustomerPreviewModalProps {
  open: boolean
  edition: EditionView | null
  projectLabel: string
  preparedFor?: string | null
  sharedBy?: string | null
  dateFormat: string
  onClose: () => void
}

/**
 * Screen 5's "Preview as customer" — the exact public-page body
 * (`ProjectEducationGuideBody`, shared with Task 9's real customer page)
 * rendered read-only, with a preview banner in place of any live action:
 * no audio playback, no Education Center, no contact/report-issue footer.
 */
export function CustomerPreviewModal({
  open,
  edition,
  projectLabel,
  preparedFor,
  sharedBy,
  dateFormat,
  onClose
}: CustomerPreviewModalProps) {
  const { t } = useTranslation()

  return (
    <Modal
      open={open && !!edition}
      title={t("projectEducation.customerPreviewModal.title")}
      onCancel={onClose}
      footer={[
        <Button
          key={"close"}
          onClick={onClose}
          data-testid={"customer-preview-modal-close"}
        >
          {t("projectEducation.customerPreviewModal.close")}
        </Button>
      ]}
      width={720}
      destroyOnHidden
      data-testid={"customer-preview-modal"}
    >
      {edition && (
        <ProjectEducationGuideBody
          projectLabel={projectLabel}
          preparedFor={preparedFor}
          sharedBy={sharedBy}
          updatedLabel={formatEditionDate(
            edition.published_at ?? edition.created_at,
            dateFormat
          )}
          html={edition.html}
          citations={edition.citations}
          limitations={edition.limitations}
          banner={
            <Alert
              type={"info"}
              showIcon
              className={"mb-3"}
              message={t("projectEducation.customerPreviewModal.banner")}
              data-testid={"customer-preview-banner"}
            />
          }
          labels={{
            guideTitle: (label) =>
              t("projectEducation.guideBody.titleFor", { projectLabel: label }),
            preparedFor: t("projectEducation.guideBody.preparedFor"),
            sharedBy: t("projectEducation.guideBody.sharedBy"),
            updated: t("projectEducation.guideBody.updated"),
            sourcesHeading: t("projectEducation.guideBody.sourcesHeading"),
            disclaimer: t("projectEducation.guideBody.disclaimer"),
            articleLabels: {
              viewSources: (count) =>
                t("projectEducation.article.viewSources", { count }),
              sourcesLabel: t("projectEducation.article.sourcesLabel")
            }
          }}
        />
      )}
    </Modal>
  )
}
